<template>
    <div v-if="visible" class="legal-banner" role="region" aria-label="Updated privacy policy and terms">
        <div class="legal-banner__inner">
            <div class="legal-banner__text">
                <h4 class="legal-banner__title">We have rewritten our privacy policy and terms</h4>
                <p>
                    The new wording explains how recordings of a learner's speech are stored, who can
                    listen to them, what our speech software does with them, and how long we keep them.
                    It also makes clear that we do not use recordings to train AI, and that permission to
                    help improve the AI is a separate setting your organization controls. Nothing about
                    how you run an assessment has changed.
                </p>
                <p class="legal-banner__consequence">
                    Please accept to carry on using the AI features: automatic transcription and the
                    suggested practice words are paused until you do. Everything else works as normal.
                </p>
                <p class="legal-banner__links">
                    <RouterLink v-for="doc in pendingDocuments" :key="doc.key" :to="doc.route" target="_blank">
                        Read the {{ doc.title.toLowerCase() }}
                    </RouterLink>
                </p>
            </div>

            <div class="legal-banner__actions">
                <label class="legal-banner__check">
                    <input type="checkbox" class="rlms-checkbox" v-model="agreed" />
                    <span>I have read and accept both</span>
                </label>
                <div class="legal-banner__buttons">
                    <button type="button" class="rlms-btn rlms-btn-primary" :disabled="!agreed || legal.submitting"
                        @click="onAccept">
                        {{ legal.submitting ? 'Saving...' : 'Accept' }}
                    </button>
                    <button type="button" class="rlms-btn rlms-btn-outline-primary" :disabled="legal.submitting"
                        @click="legal.dismiss()">
                        Not now
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useLegalStore } from '@/stores/legal';
import { handleError } from '@/utils/handleError';
import { showToast } from '@/utils/swal';

const legal = useLegalStore();
const agreed = ref(false);

// Nothing is shown while the server does not record acceptances, so this can
// ship before the backend half without locking anyone out of the AI features.
const visible = computed(() => legal.needsAcceptance && !legal.dismissed);
const pendingDocuments = computed(() => legal.pendingDocuments);

const onAccept = async () => {
    if (!agreed.value) return;

    try {
        await legal.accept();

        showToast({
            icon: 'success',
            title: 'Thank you',
            text: 'Your acceptance has been recorded.',
        });
    } catch (error) {
        handleError(error);
    }
};
</script>

<style scoped>
.legal-banner {
    position: fixed;
    inset-inline: 0;
    bottom: 0;
    z-index: 1000;
    background: #ffffff;
    border-top: 2px solid #725CFF;
    box-shadow: 0 -6px 24px rgba(16, 24, 40, 0.12);
    padding: 16px 20px;
}

.legal-banner__inner {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
}

.legal-banner__title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 6px;
}

.legal-banner__text p {
    font-size: 13px;
    line-height: 19px;
    margin: 0 0 6px;
}

.legal-banner__consequence {
    font-weight: 500;
}

.legal-banner__links {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.legal-banner__links a {
    font-size: 13px;
    font-weight: 500;
    text-decoration: underline;
}

.legal-banner__actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 0 0 auto;
}

.legal-banner__check {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    user-select: none;
}

.legal-banner__buttons {
    display: flex;
    gap: 10px;
}

@media (max-width: 767px) {
    .legal-banner {
        padding: 14px 16px;
        max-height: 70vh;
        overflow-y: auto;
    }

    .legal-banner__inner {
        flex-direction: column;
        gap: 14px;
    }

    .legal-banner__actions {
        width: 100%;
    }

    .legal-banner__buttons {
        flex-direction: column;
    }

    .legal-banner__buttons .rlms-btn {
        width: 100%;
    }
}
</style>
